/**
 * Constants for `LandingPage`.
 *
 * @module @modules/landing/constants
 */

import { BarChart3, FileText, Play, Flame, MessageCircle, Sparkles, TrendingUp, Trophy, User, Lightbulb } from 'lucide-react';
import { type TranslationKey } from '@infra/i18n';
import { brandColors } from '@shared/constants';

/**
 * LandingPage
 *
 * Design system applied:
 * - Glassmorphism (Layer System): high-blur semi-transparent surfaces, hairline white
 *   borders, two-tier shadow stack (ambient + key) elevating content on top of the base.
 * - Soft Gradient (Mood System): cool sky-tint base (#F5FBFE) accented with
 *   an iridescent liquid orb — the signature visual cue used in premium AI products.
 * - Primary brand gradient: #1BB6E7 → #0E94C9 → #0078B5 (cyan → sky → deep blue).
 */

export const HERO_PORTRAIT = '/persona-olivia.jpg';

export const PERSONAS: Array<{
  nameKey: TranslationKey;
  age: number;
  cityKey: TranslationKey;
  toneKey: TranslationKey;
  trait1Key: TranslationKey;
  trait2Key: TranslationKey;
  photo: string;
  cardTint: string;
  active?: boolean;
  /** Cool down a warm-lit portrait so it matches the sky palette */
  coolPhoto?: boolean;
}> = [
  {
    nameKey: 'landing:personas.emma.name',
    age: 24,
    cityKey: 'landing:personas.emma.city',
    toneKey: 'landing:personas.emma.tone',
    trait1Key: 'landing:personas.emma.trait1',
    trait2Key: 'landing:personas.emma.trait2',
    photo: '/persona-emma.jpg',
    cardTint: 'from-tone-card-tint/70 to-tone-card-tint-end/30',
  },
  {
    nameKey: 'landing:personas.daniel.name',
    age: 28,
    cityKey: 'landing:personas.daniel.city',
    toneKey: 'landing:personas.daniel.tone',
    trait1Key: 'landing:personas.daniel.trait1',
    trait2Key: 'landing:personas.daniel.trait2',
    photo: '/persona-daniel.jpg',
    cardTint: 'from-tone-card-tint-deep/70 to-tone-card-tint-strong/30',
    active: true,
  },
  {
    nameKey: 'landing:personas.sophia.name',
    age: 26,
    cityKey: 'landing:personas.sophia.city',
    toneKey: 'landing:personas.sophia.tone',
    trait1Key: 'landing:personas.sophia.trait1',
    trait2Key: 'landing:personas.sophia.trait2',
    photo: '/persona-sophia.jpg',
    cardTint: 'from-tone-card-tint/70 to-tone-card-tint-end/30',
    coolPhoto: true,
  },
  {
    nameKey: 'landing:personas.jake.name',
    age: 29,
    cityKey: 'landing:personas.jake.city',
    toneKey: 'landing:personas.jake.tone',
    trait1Key: 'landing:personas.jake.trait1',
    trait2Key: 'landing:personas.jake.trait2',
    photo: '/persona-jake.jpg',
    cardTint: 'from-tone-card-tint-strong/70 to-tone-card-tint-end-alt/30',
  },
  {
    nameKey: 'landing:personas.olivia.name',
    age: 25,
    cityKey: 'landing:personas.olivia.city',
    toneKey: 'landing:personas.olivia.tone',
    trait1Key: 'landing:personas.olivia.trait1',
    trait2Key: 'landing:personas.olivia.trait2',
    photo: '/persona-olivia.jpg',
    cardTint: 'from-tone-card-tint/70 to-tone-card-tint/30',
  },
];

export const DEMO_AVATAR_FALLBACKS = {
  sophia: 'https://i.pravatar.cc/96?img=32',
  emma: 'https://i.pravatar.cc/96?img=12',
  daniel: 'https://i.pravatar.cc/96?img=68',
  olivia: 'https://i.pravatar.cc/96?img=47',
  jake: 'https://i.pravatar.cc/96?img=15',
} as const;

export const ROLEPLAY_INPUT_PLACEHOLDER_KEY = 'landing:hero.inputPlaceholder';

export const PERSONAS_VISIBLE_COUNT = 4;

export const PERSONAS_AUTO_INTERVAL_MS = 3000;

export const JOURNEY_STEPS = [
  {
    step: 1,
    titleKey: 'landing:journey.step1.title',
    descriptionKey: 'landing:journey.step1.description',
    icon: User,
  },
  {
    step: 2,
    titleKey: 'landing:journey.step2.title',
    descriptionKey: 'landing:journey.step2.description',
    icon: FileText,
  },
  {
    step: 3,
    titleKey: 'landing:journey.step3.title',
    descriptionKey: 'landing:journey.step3.description',
    icon: Play,
  },
  {
    step: 4,
    titleKey: 'landing:journey.step4.title',
    descriptionKey: 'landing:journey.step4.description',
    icon: BarChart3,
  },
  {
    step: 5,
    titleKey: 'landing:journey.step5.title',
    descriptionKey: 'landing:journey.step5.description',
    icon: Sparkles,
  },
] as const;

export const JOURNEY_PERSONA_OPTIONS = [
  {
    nameKey: 'landing:persona.sophia.name' as const,
    age: 42,
    genderKey: 'landing:persona.sophia.gender' as const,
    photo: '/persona-sophia.jpg',
    tags: [
      'landing:persona.sophia.tag.entrepreneur',
      'landing:persona.sophia.tag.timePoor',
    ] as const,
    description: 'landing:persona.sophia.description' as const,
    selected: true,
  },
  {
    nameKey: 'landing:persona.daniel.name' as const,
    age: 55,
    genderKey: 'landing:persona.daniel.gender' as const,
    photo: '/persona-daniel.jpg',
    tags: ['landing:persona.daniel.tag.investor', 'landing:persona.daniel.tag.analytical'] as const,
    description: 'landing:persona.daniel.description' as const,
    selected: false,
  },
];

export const JOURNEY_PERSONA_PAGE_COUNT = 5;

export const JOURNEY_GLASS_PANEL =
  `relative overflow-hidden border border-tone-glass/35 backdrop-blur-[24px] backdrop-saturate-[1.55] shadow-[0_20px_48px_-18px_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.24)),inset_0_1px_0_rgba(var(--brand-rgb-surface-card),calc(var(--shadow-strength)*0.92)),inset_0_calc(-16px)_32px_calc(-14px)_rgba(var(--brand-rgb-surface-card),calc(var(--shadow-strength)*0.24))]`;

export const JOURNEY_GLASS_SOFT =
  `border border-tone-glass/45 backdrop-blur-2xl backdrop-saturate-[1.45] shadow-[0_16px_40px_-16px_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.2)),inset_0_1px_0_rgba(var(--brand-rgb-surface-card),calc(var(--shadow-strength)*0.94)),inset_0_calc(-10px)_20px_calc(-10px)_rgba(var(--brand-rgb-surface-card),calc(var(--shadow-strength)*0.2))]`;

export const JOURNEY_CAROUSEL_INTERVAL_MS = 3000;

export const JOURNEY_CAROUSEL_TRANSITION = {
  duration: 0.55,
  ease: [0.25, 0.1, 0.25, 1] as const,
};

export const PROGRESS_SUMMARY_STATS = [
  {
    value: '12',
    labelKey: 'landing:progress.stats.completed',
    icon: User,
    iconWrap: 'bg-tone-accent-wash',
    iconColor: `text-[var(--brand-accent-text)]`,
    valueColor: `text-[var(--brand-accent-text)]`,
  },
  {
    value: '8.6',
    labelKey: 'landing:progress.stats.avgScore',
    icon: TrendingUp,
    iconWrap: 'bg-tone-positive-surface',
    iconColor: 'text-tone-positive',
    valueColor: 'text-tone-positive',
  },
  {
    value: '4',
    labelKey: 'landing:progress.stats.dayStreak',
    icon: Flame,
    iconWrap: 'bg-tone-streak-surface',
    iconColor: 'text-tone-streak',
    valueColor: 'text-tone-streak',
  },
  {
    value: 'Top 15%',
    labelKey: 'landing:progress.stats.amongUsers',
    icon: Trophy,
    iconWrap: 'bg-tone-achievement-surface',
    iconColor: 'text-tone-achievement',
    valueColor: `text-[var(--brand-accent-deep)]`,
  },
] as const;

export const PROGRESS_RECENT_SESSIONS = [
  {
    nameKey: 'landing:progress.recentSessions.session1.name',
    score: '82/100',
    date: 'May 24, 2024',
  },
  {
    nameKey: 'landing:progress.recentSessions.session2.name',
    score: '75/100',
    date: 'May 22, 2024',
  },
  {
    nameKey: 'landing:progress.recentSessions.session3.name',
    score: '90/100',
    date: 'May 20, 2024',
  },
] as const;

export const JOURNEY_STEP_COUNT = JOURNEY_STEPS.length;

export const SCENARIO_MOCK = {
  title: 'landing:scenario.mock.title',
  situation: 'landing:scenario.mock.situation',
  objective: 'landing:scenario.mock.objective',
  difficulty: 'landing:scenario.mock.difficulty',
  talkingPoints: [
    'landing:scenario.mock.talkingPoints.understandConcern',
    'landing:scenario.mock.talkingPoints.explainPremiumFactors',
    'landing:scenario.mock.talkingPoints.highlightValueCoverage',
    'landing:scenario.mock.talkingPoints.offerSolutions',
  ],
} as const;

export const ROLEPLAY_SESSION = {
  personaName: 'Small Business Owner',
  personaPhoto: DEMO_AVATAR_FALLBACKS.sophia,
  coachPhoto: DEMO_AVATAR_FALLBACKS.emma,
  progress: 65,
  messages: [
    {
      role: 'persona' as const,
      text: 'landing:roleplay.session.message1',
      time: '09:41 AM',
    },
    {
      role: 'coach' as const,
      text: 'landing:roleplay.session.message2',
      time: '09:42 AM',
    },
    {
      role: 'persona' as const,
      text: 'landing:roleplay.session.message3',
      time: '09:43 AM',
    },
    {
      role: 'coach' as const,
      text: 'landing:roleplay.session.message4',
      time: '09:44 AM',
    },
  ],
  tips: [
    { icon: MessageCircle, text: 'landing:roleplay.tips.askQuestions' },
    { icon: User, text: 'landing:roleplay.tips.showEmpathy' },
    { icon: Lightbulb, text: 'landing:roleplay.tips.explainSimply' },
    { icon: Sparkles, text: 'landing:roleplay.tips.highlightValue' },
  ],
} as const;

export const AI_FEEDBACK_MOCK = {
  score: 82,
  skills: [
    { labelKey: 'landing:aiFeedback.skills.needsDiscovery', value: 88, color: brandColors.chartSeries1 },
    { labelKey: 'landing:aiFeedback.skills.objectionHandling', value: 85, color: brandColors.chartSeries2 },
    { labelKey: 'landing:aiFeedback.skills.solutionPresentation', value: 78, color: brandColors.chartSeries3 },
    { labelKey: 'landing:aiFeedback.skills.closing', value: 70, color: brandColors.chartSeries4 },
    { labelKey: 'landing:aiFeedback.skills.empathyRapport', value: 90, color: brandColors.chartSeries5 },
  ],
  strengths: [
    'landing:aiFeedback.strengths.askedQuestions',
    'landing:aiFeedback.strengths.answeredConcern',
    'landing:aiFeedback.strengths.builtEmpathy',
  ],
  improvements: [
    'landing:aiFeedback.improvements.specificBenefits',
    'landing:aiFeedback.improvements.closingStatements',
  ],
} as const;
